export const PENTEST_WORKFLOWS = ['pentest', 'pentest-retest'] as const;
export type PentestWorkflowName = (typeof PENTEST_WORKFLOWS)[number];

export const PENTEST_SEVERITIES = ['high', 'medium', 'low'] as const;
export type PentestSeverity = (typeof PENTEST_SEVERITIES)[number];

export const PENTEST_EFFORTS = ['low', 'medium', 'high'] as const;
export type PentestEffort = (typeof PENTEST_EFFORTS)[number];

export const PENTEST_RETEST_STATUSES = [
  'fixed',
  'still-open',
  'needs-manual-verification',
] as const;
export type PentestRetestStatus = (typeof PENTEST_RETEST_STATUSES)[number];

export const PENTEST_FINDING_STATUSES = [
  'open',
  'coverage-gap',
  'fixed',
  'still-open',
  'needs-manual-verification',
  'potentially-fixed',
] as const;
export type PentestFindingStatus = (typeof PENTEST_FINDING_STATUSES)[number];

export const PENTEST_SOURCE_TYPES = ['docs', 'tests', 'code', 'runtime', 'advisory'] as const;
export type PentestSourceType = (typeof PENTEST_SOURCE_TYPES)[number];

export const PENTEST_RUN_STEP_STATUSES = [
  'not_started',
  'running',
  'completed',
  'blocked',
  'failed',
] as const;
export type PentestRunStepStatus = (typeof PENTEST_RUN_STEP_STATUSES)[number];

export const PENTEST_RUN_STATUSES = ['running', 'completed', 'blocked', 'failed'] as const;
export type PentestRunStatus = (typeof PENTEST_RUN_STATUSES)[number];

export interface PentestFinding {
  id: string;
  title: string;
  description: string;
  impact: PentestSeverity;
  effort: PentestEffort;
  possible_solution_direction: string;
  how_to_reproduce: string[];
  impact_area: string[];
  evidence: string[];
  category: string;
  status: PentestFindingStatus;
  confidence: number;
  source_types: PentestSourceType[];
  affected_modules: string[];
  affected_packages: string[];
  runtime_required: boolean;
  manual_follow_up: boolean;
}

export interface PentestRetestFinding extends PentestFinding {
  retest_status: PentestRetestStatus;
}

export interface PentestCoverageSummary {
  docs: boolean;
  tests: boolean;
  code: boolean;
  runtime: boolean;
  database: boolean;
  blocked_checks: string[];
}

export interface PentestRuntimeStatus {
  target_url: string | null;
  status: 'reachable' | 'unreachable' | 'not-configured' | 'blocked';
  detail: string;
}

export interface PentestReportIndex {
  schema_version: '1';
  generated_by: 'paqad-ai';
  framework_version: string;
  report_id: string;
  workflow: PentestWorkflowName;
  generated_at: string;
  report_path: string;
  sidecar_path: string;
  source_report_path: string | null;
  source_report_id: string | null;
  stack: {
    primary: string;
    traits: string[];
    toolchains: string[];
  };
  runtime: PentestRuntimeStatus;
  coverage: PentestCoverageSummary;
  findings: Array<PentestFinding | PentestRetestFinding>;
  blocked_checks: string[];
  sources_used: string[];
  methodology: string[];
  next_remediation_priorities: string[];
  raw_evidence_paths: string[];
}

export interface PentestRunStep {
  id: string;
  title: string;
  status: PentestRunStepStatus;
  input_hash: string | null;
  artifact_paths: string[];
  skill_names: string[];
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface PentestRunProgress {
  schema_version: '1';
  generated_by: 'paqad-ai';
  framework_version: string;
  run_id: string;
  workflow: PentestWorkflowName;
  status: PentestRunStatus;
  started_at: string;
  updated_at: string;
  report_path: string | null;
  sidecar_path: string | null;
  target_url: string | null;
  source_report_path: string | null;
  steps: PentestRunStep[];
  skill_invocations: string[];
  script_artifacts: string[];
  current_finding_ids: string[];
  incremental_type?: 'full' | 'incremental';
}
